import { useState } from 'react';
import { signupFields } from '../constants/formFields';
import Input from './Input';
import FormAction from './FormAction';
import { Link } from 'react-router-dom';
import FormSocial from './FormSocial';
import { Errors } from '../constants/errors';

interface FormElements extends HTMLFormControlsCollection {
  emailAddress: HTMLInputElement;
  password: HTMLInputElement;
  confirmPassword: HTMLInputElement;
  firstName: HTMLInputElement;
  lastName: HTMLInputElement;
}
interface SignupFormElement extends HTMLFormElement {
  readonly elements: FormElements;
}

// Source: https://emailregex.com/
// For 99.99% RFC 5322 compliance
// eslint-disable-next-line no-control-regex
const emailRegex = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/

const fields = signupFields;
const fieldsState: { [key: string]: string } = {};
fields.forEach((field) => (fieldsState[field.id] = ''));

export default function Signup() {
  const [signupState, setSignupState] = useState(fieldsState);
  const [formErrorState, setFormErrorState] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const btn = e.target as HTMLInputElement;
    setSignupState({ ...signupState, [btn.id]: btn.value });
  };

  const getLoginUrl = (): string => {
    // return `/login?email=${encodeURIComponent(signupState.emailAddress)}`;
    // TODO this is a hack for now, we need to fix
    return `/oauth/authorize${window.location.search}`;
  };

  const handleSubmit = async (event: React.FormEvent<SignupFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    if (
      emailRegex.exec(event.currentTarget.elements.emailAddress.value) === null
    ) {
      setFormErrorState(Errors.InvalidEmailAddressError);
      setSubmitting(false);
      return;
    }
    if (
      event.currentTarget.elements.password.value !==
      event.currentTarget.elements.confirmPassword.value
    ) {
      setFormErrorState(Errors.PasswordsDoNotMatchError);
      setSubmitting(false);
      return;
    }

    // create a new user
    const response = await fetch(`/api/users${window.location.search}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/x-www-form-urlencoded;charset=UTF-8',
        accept: 'application/json',
      },
      body: new URLSearchParams({
        email: event.currentTarget.elements.emailAddress.value,
        firstName: event.currentTarget.elements.firstName.value,
        lastName: event.currentTarget.elements.lastName.value,
        password: event.currentTarget.elements.password.value,
      }),
    });
    try {
      const payload = await response.json();
      if (
        response.status !== 200 &&
        payload.error &&
        payload.type === 'UNKNOWN_ERROR'
      ) {
        // show an error to the user
        setFormErrorState(Errors.UnknownError);
        setSubmitting(false);
      } else {
        setFormErrorState('');
        // redirect the user here
        // TODO: Get the above fetch call to return a url to redirect to based on where they have come from
        if (payload.redirectUrl) {
          window.location.href = payload.redirectUrl;
        } else {
          // navigate('/account');
          // TODO this is a hack for now, we need to fix
          window.location.href = 'https://app.bloodygoodtests.com.au';
        }
        setSubmitting(false);
      }
    } catch (e) {
      console.log(e);
      setFormErrorState(Errors.ServerError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      {formErrorState && (
        <div
          className="bg-eirly-100/10 border-t-4 border-eirly-500 rounded-b text-teal-900 px-4 py-3 shadow-md"
          role="alert"
        >
          <div className="flex">
            <div className="py-1">
              <svg
                className="fill-current h-6 w-6 text-eirly-500 mr-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z" />
              </svg>
            </div>
            <div>
              <p className="font-bold">Error</p>

              {formErrorState === Errors.UnknownError && (
                <p className="text-sm">
                  Unable to create account, perhaps try{' '}
                  <Link
                    className="text-eirly-600 hover:text-eirly-500"
                    to={getLoginUrl()}
                  >
                    logging in
                  </Link>{' '}
                  instead?
                </p>
              )}
              {formErrorState === Errors.ServerError && (
                <p className="text-sm">
                  Server returned an error, please try again later
                </p>
              )}
              {formErrorState === Errors.PasswordsDoNotMatchError && (
                <p className="text-sm">The passwords do not match</p>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="-space-y-px">
        {fields.map((field) => (
          <Input
            key={field.id}
            handleChange={handleChange}
            value={signupState[field.id]}
            labelText={field.labelText}
            labelFor={field.labelFor}
            id={field.id}
            name={field.name}
            type={field.type}
            isRequired={field.isRequired}
            placeholder={field.placeholder}
            note={field.note}
            tabIndex={field.tabIndex}
          />
        ))}
      </div>
      <FormAction text="Signup" disabled={submitting} tabIndex={5} />
      <FormSocial />

      <p className="text-sm text-gray-500 text-center self-center mt-6">
        Already have an account?{' '}
        <Link
          className="underline"
          to={`/oauth/authorize${window.location.search}`}
        >
          Login
        </Link>
      </p>
    </form>
  );
}
